'use client';

/**
 * Nothing on the translator renders until we know who is asking.
 *
 * ── What it is, and what it is not ──────────────────────────────────────────
 * This is the VISIBLE half of the gate: it sends somebody who is not signed in
 * to /login instead of showing them a translator they cannot use. The enforcing
 * half is on the server (backend/src/middleware/requireAuth.ts), because the API
 * is on the public internet and a script never loads this page at all. Neither
 * half is redundant — without the server there is no security, and without this
 * there is a page full of controls that answer 401.
 *
 * ── Why it renders nothing while it waits ───────────────────────────────────
 * The answer arrives after mount (and may involve one automatic sign-in from
 * stored credentials, see lib/auth.tsx). Rendering the translator in the
 * meantime and pulling it away would be worse than a beat of quiet — and
 * rendering "you are not signed in" first would flash a lie at every returning
 * user, which is most of them.
 */

import { useEffect, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth';
import { BRAND } from '@/lib/brand';
import { useWait } from '@/lib/wait';

export default function AuthGate({ children }: { children: ReactNode }) {
  const { loaded, user, unavailable, guest } = useAuth();
  const router = useRouter();
  /*
   * A guest (2026-10-06): no account, but the server hands out free
   * translations (backend lib/guestCredits.ts). They get the translator — even
   * at 0 left, so they can still read the page; the sign-up prompt
   * (GuestPrompt) is what stops them, not a redirect.
   */
  const guestOk = !!guest && guest.total > 0;

  /*
   * The beat of quiet this gate insists on (see "Why it renders nothing while
   * it waits") is still nothing from THIS component — but it is no longer a
   * blank window. The global wait holds the screen for it, and says which of
   * the two waits this is: finding out who is asking, or already on the way to
   * the sign-in page.
   */
  const leaving = loaded && !user && !guestOk && !unavailable;
  useWait(!loaded, 'Checking your sign-in');
  useWait(leaving, 'Taking you to sign in');

  useEffect(() => {
    // `unavailable` is excluded deliberately: a failed fetch is not evidence
    // that somebody is signed out, and bouncing them to a login page they also
    // cannot use would turn a backend hiccup into a lost session.
    if (loaded && !user && !guestOk && !unavailable) router.replace('/login');
  }, [loaded, user, guestOk, unavailable, router]);

  if (loaded && unavailable) {
    return (
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center px-4 text-center">
        <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">
          {BRAND.name} is not reachable right now.
        </p>
        <p className="mt-1.5 text-sm text-ink-600 dark:text-ink-300">
          Nothing is wrong with your account — the server did not answer. Try again in a moment.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 rounded-xl border border-ink-300 px-4 py-2 text-sm font-semibold
                     text-ink-700 transition hover:border-accent-400 hover:text-accent-700
                     dark:border-ink-700 dark:text-ink-200 dark:hover:border-accent-500
                     dark:hover:text-accent-300"
        >
          Try again
        </button>
      </main>
    );
  }

  // Waiting, or already on the way to /login. Deliberately blank: see above.
  if (!loaded || (!user && !guestOk)) return null;

  return <>{children}</>;
}
